import { useCallback, useEffect, useMemo, useRef, useState, type Dispatch, type SetStateAction } from "react";
import { useWorkspaceLocalStorage } from "./useWorkspaceLocalStorage";

export type ColumnType = "ai" | "checkbox" | "currency" | "date" | "single-select" | "multi-select" | "email" | "location" | "number" | "phone" | "text" | "traffic-light" | "url" | "formula";

export interface ColumnDef {
  key: string;
  label: string;
  sticky?: boolean;
  type?: ColumnType;
  aiPrompt?: string;
  selectOptions?: string[];
  /** AI columns: raw backend `fieldConfig` JSON, so a prompt edit can merge into it. */
  fieldConfig?: string | null;
  /**
   * Hide this column the first time a persisted snapshot encounters it — for
   * columns worth shipping but not worth spending default grid width on. The
   * user's later Manage Columns choice always wins.
   *
   * Applies to existing snapshots, not just fresh ones: a column added to a grid
   * after users already have saved layouts would otherwise appear visible for
   * everyone (their `hiddenKeys` predates the key), and bumping the storage key to
   * force the default would wipe every saved layout. See `freshDefaultHiddenKeys`.
   */
  hiddenByDefault?: boolean;
}

export type SortDirection = "asc" | "desc" | null;

export const DEFAULT_COLUMN_WIDTH = 180;

const COLUMN_TYPE_WIDTHS: Record<ColumnType, number> = {
  text: 180,
  email: 240,
  url: 200,
  phone: 140,
  location: 180,
  number: 100,
  currency: 120,
  date: 120,
  checkbox: 80,
  "single-select": 140,
  "multi-select": 200,
  "traffic-light": 100,
  ai: 180,
  formula: 140,
};

export interface TableColumnState {
  visibleColumns: ColumnDef[];
  allColumns: ColumnDef[];
  hiddenKeys: Set<string>;
  sortCol: string | null;
  sortDir: SortDirection;
  columnOrder: string[];
  columnWidths: Record<string, number>;
  /** Effective render width: user override → frozen (measured-once) width → per-key default → per-type default → fallback. */
  getColumnWidth: (key: string) => number;
  /** Sum of getColumnWidth across visibleColumns (excludes leading/filler cells). */
  totalContentWidth: number;
  /** True once column widths have been measured-and-frozen for this mount. */
  isFrozen: boolean;
  /**
   * Freeze the given measured widths as the source of truth. Idempotent: only the
   * first call per mount takes effect (subsequent calls are no-ops). Manual resize
   * overrides still win over frozen widths.
   */
  freezeColumnWidths: (measured: Record<string, number>) => void;
  toggleSort: (key: string, dir: SortDirection) => void;
  hideColumn: (key: string) => void;
  showColumn: (key: string) => void;
  reorderColumns: (fromKey: string, toKey: string) => void;
  sortData: <T>(data: T[]) => T[];
  addColumn: (col: ColumnDef) => void;
  removeColumn: (key: string) => void;
  setColumnWidth: (key: string, width: number) => void;
  updateColumn: (key: string, updates: Partial<Pick<ColumnDef, "label" | "type" | "selectOptions" | "aiPrompt">>) => void;
}

interface PersistedState {
  hiddenKeys: string[];
  columnOrder: string[];
  columnWidths: Record<string, number>;
  sortCol: string | null;
  sortDir: SortDirection;
  customColumns: ColumnDef[];
}

interface TableColumnOptions {
  defaultSort?: string;
  numericKeys?: string[];
  sortTransforms?: Record<string, (val: any) => any>;
  storageKey?: string;
  defaultWidths?: Record<string, number>;
  /**
   * When true, persisted `custom_*` columns that are absent from the current
   * `initialColumns` are pruned from localStorage. Use it on surfaces where
   * every custom column is backend-backed (and supplied via initialColumns), so
   * a leftover persisted column means the backing field was deleted or never
   * persisted — a "ghost field" that shows in the grid but not in detail sheets.
   * Safe because backend-backed columns render from initialColumns regardless of
   * localStorage, so pruning only removes genuine orphans. Columns created via
   * addColumn during the current session are exempt (their backend field exists
   * but initialColumns may not have refetched yet). Leave false (default) in
   * preview mode and on surfaces with legitimately local-only columns.
   */
  pruneOrphanCustomColumns?: boolean;
}

/**
 * The `columnOrder` as literally stored, or `[]` when the snapshot has none.
 *
 * Distinct from `normalizePersisted`, which substitutes a full default order for a
 * missing field — right for ordering, wrong for "has this snapshot met the
 * column?". A legacy entry like `{ hiddenKeys: [...] }` would otherwise inherit a
 * default order that already lists every current column, making a brand-new
 * `hiddenByDefault` column look already-seen and skipping its default.
 */
function storedColumnOrder(raw: unknown): string[] {
  const order = (raw as Partial<PersistedState> | null | undefined)?.columnOrder;
  return Array.isArray(order) ? order : [];
}

/**
 * Columns flagged `hiddenByDefault` that this snapshot has never seen — their key
 * is absent from the stored `columnOrder`, so the snapshot predates the column and
 * carries no user opinion about it. Hidden on top of `persisted.hiddenKeys`.
 *
 * `columnOrder` is the "has this snapshot met the column?" marker rather than a
 * separate list because the hook already persists it (`reorderColumns`,
 * `addColumn`) and already merges new keys into it at render. The hook writes the
 * fresh keys into it — see the materialize effect — which is what makes the
 * default one-shot: once written, the column is ordinary persisted state and
 * `showColumn` alone keeps the user's choice.
 *
 * Note this is deliberately NOT the shape of the `custom_*` force-unhide effect
 * that used to live in this file (removed; see the comment where it was). That one
 * tracked "seen" in a session ref it never persisted, so every reload re-derived
 * the key as new and clobbered the user's choice. Persisting the marker is the
 * whole difference.
 */
function freshDefaultHiddenKeys(initialColumns: ColumnDef[], columnOrder: string[]): string[] {
  const orderSet = new Set(columnOrder);
  return initialColumns.filter(c => c.hiddenByDefault && !orderSet.has(c.key)).map(c => c.key);
}

function makeInitialPersisted(initialColumns: ColumnDef[], options?: TableColumnOptions): PersistedState {
  return {
    // A fresh snapshot meets every initial column at once, so the flag applies here
    // directly and `freshDefaultHiddenKeys` finds nothing left to do.
    hiddenKeys: initialColumns.filter(c => c.hiddenByDefault).map(c => c.key),
    columnOrder: initialColumns.map(c => c.key),
    // columnWidths is "user resize overrides only"; framework defaults live in
    // options.defaultWidths and per-type widths and are resolved at render via getColumnWidth.
    columnWidths: {},
    sortCol: options?.defaultSort ?? null,
    sortDir: options?.defaultSort ? "asc" : null,
    customColumns: [],
  };
}

// Reconcile a persisted snapshot read from localStorage against the current
// PersistedState shape. localStorage entries can predate a field (e.g. a stored
// value written before `customColumns` existed, under a reused storage key), and
// readKey returns the parsed JSON verbatim with no merge — so an absent field
// arrives as `undefined` and blows up the first `.filter`/`.map` that touches it.
// Coerce every field back to a valid shape, falling back to defaults, so both the
// render-time memos and the functional setter updaters always see safe arrays.
function normalizePersisted(
  state: Partial<PersistedState> | null | undefined,
  defaults: PersistedState,
): PersistedState {
  if (!state || typeof state !== "object") return defaults;
  return {
    hiddenKeys: Array.isArray(state.hiddenKeys) ? state.hiddenKeys : defaults.hiddenKeys,
    columnOrder: Array.isArray(state.columnOrder) ? state.columnOrder : defaults.columnOrder,
    columnWidths:
      state.columnWidths && typeof state.columnWidths === "object" ? state.columnWidths : {},
    sortCol: state.sortCol ?? defaults.sortCol,
    sortDir: state.sortDir ?? defaults.sortDir,
    customColumns: Array.isArray(state.customColumns) ? state.customColumns : [],
  };
}

/** localStorage suffix (workspace-scoped via useWorkspaceLocalStorage) for a table's column prefs. */
export function tableColumnsStorageSuffix(storageKey: string): string {
  return `table_cols_${storageKey}`;
}

/**
 * Pure snapshot of the visibleColumns derivation in useTableColumns: persisted
 * snapshot (normalized) → effective column order (persisted order + new initial
 * keys appended) → initial + persisted custom columns → minus hidden keys.
 *
 * Used by the idle prefetch (use-idle-prefetch.ts) to predict, WITHOUT mounting
 * the grid, exactly which columns a page's useTableColumns will report visible —
 * so prefetched gridSummary variables match the page's own request byte-for-byte.
 * useTableColumns remains the source of truth; the parity test in
 * useTableColumns.test.ts asserts this function tracks the hook's output.
 *
 * One deliberate difference from the hook: defaults derive from the passed
 * `initialColumns` rather than the hook's first-render columns. The hook's first
 * render usually sees standard columns only (custom fields still loading) and
 * appends custom keys once they arrive — which yields the same final order as
 * passing the full standard+custom list here.
 */
export function deriveVisibleColumns(
  initialColumns: ColumnDef[],
  rawPersisted: unknown,
  options?: Pick<TableColumnOptions, "defaultSort">,
): ColumnDef[] {
  // normalizePersisted validates every field, so raw parsed JSON is safe here.
  const persisted = normalizePersisted(
    rawPersisted as Partial<PersistedState> | null | undefined,
    makeInitialPersisted(initialColumns, options),
  );

  const orderSet = new Set(persisted.columnOrder);
  const missing = initialColumns.filter(c => !orderSet.has(c.key)).map(c => c.key);
  const effectiveColumnOrder =
    missing.length === 0 ? persisted.columnOrder : [...persisted.columnOrder, ...missing];

  const initialKeys = new Set(initialColumns.map(c => c.key));
  const customs = persisted.customColumns.filter(c => !initialKeys.has(c.key));
  const colMap = new Map([...initialColumns, ...customs].map(c => [c.key, c]));

  // Mirrors the hook's hiddenKeys memo: persisted hidden keys plus any
  // `hiddenByDefault` column this snapshot hasn't met yet. Computed against the
  // STORED order, not effectiveColumnOrder — the latter has already absorbed the
  // new keys, which would make every fresh default look already-seen.
  const hidden = new Set([
    ...persisted.hiddenKeys,
    ...freshDefaultHiddenKeys(initialColumns, storedColumnOrder(rawPersisted)),
  ]);
  return effectiveColumnOrder
    .map(k => colMap.get(k))
    .filter((c): c is ColumnDef => Boolean(c) && !hidden.has(c!.key));
}

export function useTableColumns(
  initialColumns: ColumnDef[],
  options?: TableColumnOptions,
): TableColumnState {
  const safeKey = options?.storageKey ?? "__no_storage__";

  // Default snapshot is computed once from the first render's initialColumns/options.
  // Persisted values from localStorage take precedence after that.
  const initialDefaultRef = useRef<PersistedState>();
  if (!initialDefaultRef.current) {
    initialDefaultRef.current = makeInitialPersisted(initialColumns, options);
  }

  const [rawPersisted, setRawPersisted] = useWorkspaceLocalStorage<PersistedState>(
    tableColumnsStorageSuffix(safeKey),
    initialDefaultRef.current,
  );

  // A legacy/partial localStorage entry can be missing fields the current shape
  // relies on; normalize on read so every downstream memo sees valid arrays.
  const persisted = useMemo(
    () => normalizePersisted(rawPersisted, initialDefaultRef.current),
    [rawPersisted],
  );

  // Functional updaters below read `prev.customColumns`/`prev.columnOrder` etc.
  // `prev` is the raw stored value, which may also be partial — normalize it
  // before handing it to the updater so the setters can't crash on the same
  // missing-field hazard the render path just guarded against.
  const setPersisted = useCallback<Dispatch<SetStateAction<PersistedState>>>(
    (action) => {
      setRawPersisted(prev =>
        typeof action === "function"
          ? (action as (p: PersistedState) => PersistedState)(
              normalizePersisted(prev, initialDefaultRef.current),
            )
          : action,
      );
    },
    [setRawPersisted],
  );

  // Hold latest initialColumns in a ref so setters can read it without going stale.
  const initialColumnsRef = useRef(initialColumns);
  initialColumnsRef.current = initialColumns;

  // Keys added via addColumn during this session. These are freshly created
  // columns whose backend field already exists (createCustomField succeeded
  // before addColumn was called), so they must NEVER be treated as orphans —
  // even though the custom-fields query that feeds initialColumns may not have
  // refetched them yet. Without this exemption, pruneOrphanCustomColumns would
  // delete a just-created column in the window before the grid's customFields
  // catches up. Orphan detection therefore only targets columns loaded from
  // persistence, not ones created in this session.
  const sessionAddedKeysRef = useRef<Set<string>>(new Set());

  // Effective column order = persisted order + any new initialColumns keys appended.
  // We do not write the merged order back unless the user explicitly reorders;
  // merging at render time keeps workspace switches clean.
  const effectiveColumnOrder = useMemo(() => {
    const orderSet = new Set(persisted.columnOrder);
    const missing = initialColumns.filter(c => !orderSet.has(c.key)).map(c => c.key);
    return missing.length === 0 ? persisted.columnOrder : [...persisted.columnOrder, ...missing];
  }, [initialColumns, persisted.columnOrder]);

  // `hiddenByDefault` columns this snapshot hasn't met yet. Derived at render (not
  // only in the effect below) so the first paint is already correct — otherwise the
  // column flashes visible for a frame before the write lands.
  const freshDefaults = useMemo(
    () => freshDefaultHiddenKeys(initialColumns, storedColumnOrder(rawPersisted)),
    [initialColumns, rawPersisted],
  );

  // Materialize those defaults: add them to `hiddenKeys`, and persist a
  // `columnOrder` that contains them.
  //
  // Persisting the order is what makes this one-shot — on the next render the keys
  // are no longer "fresh", so the default stops applying and the user's Manage
  // Columns choice (which only touches `hiddenKeys`) sticks from then on. Without
  // it, every mount would re-hide a column the user had deliberately shown.
  //
  // It writes the merged effective order rather than blindly appending, so a
  // snapshot that was missing `columnOrder` entirely gets a correct full order
  // instead of one listing only the new keys (which would jump them to the front).
  //
  // Only fires when there's something fresh, so grids whose defaults are already in
  // their snapshot keep today's behaviour exactly: render-time merge, no write.
  useEffect(() => {
    if (freshDefaults.length === 0) return;
    setPersisted(prev => {
      const hiddenSet = new Set(prev.hiddenKeys);
      const orderSet = new Set(prev.columnOrder);
      return {
        ...prev,
        hiddenKeys: [...prev.hiddenKeys, ...freshDefaults.filter(k => !hiddenSet.has(k))],
        columnOrder: [...prev.columnOrder, ...freshDefaults.filter(k => !orderSet.has(k))],
      };
    });
  }, [freshDefaults, setPersisted]);

  // For `custom_*` columns, `persisted.hiddenKeys` is the only authority on what's
  // hidden. (Standard columns can additionally be hidden by `hiddenByDefault` until
  // the snapshot has met them — see above. Custom columns never carry that flag, so
  // nothing derives hidden state for them.)
  //
  // There used to be an effect here that force-unhid any `custom_*` key the first
  // time it appeared in `initialColumns`, tracked through a session ref, so that a
  // newly created custom field couldn't arrive pre-hidden. It made hiding those
  // columns impossible. A field created in Settings → Data Model reaches the grid
  // through `initialColumns` only: it never enters `columnOrder` (that snapshot is
  // taken on the hook's FIRST render, before the custom-fields query resolves) and
  // never enters `customColumns` (that's `addColumn`'s job, for columns created
  // from the grid). So hiding one wrote the key to `hiddenKeys` alone; the next
  // page load rebuilt the ref from `columnOrder`/`customColumns`, didn't find the
  // key, read it as freshly arrived, and unhid it. Formula columns are how this
  // got reported — they're normally created in Data Model settings — but the
  // mechanism applied to any custom field created there.
  //
  // Nothing replaces it: a genuinely new field's key is absent from `hiddenKeys`,
  // so it is visible by default anyway. The one behaviour lost is that deleting a
  // hidden field and recreating it under the same `crmName` brings it back hidden,
  // which the Manage Columns toggle fixes.

  // Build the full column list: initial columns + persisted custom columns (deduped by key).
  const allColumnsList = useMemo(() => {
    const initialKeys = new Set(initialColumns.map(c => c.key));
    const customs = persisted.customColumns.filter(c => !initialKeys.has(c.key));
    return [...initialColumns, ...customs];
  }, [initialColumns, persisted.customColumns]);

  // Prune orphaned custom columns: persisted `custom_*` entries that are no
  // longer present in initialColumns (their backend field was deleted, or never
  // persisted because creation failed). They'd otherwise linger in the grid as
  // ghost columns that never show in detail sheets. Opt-in per surface; only the
  // localStorage copy is removed — backend-backed columns still render from
  // initialColumns, so legitimate columns are never lost. Columns created in
  // this session (sessionAddedKeysRef) are exempt: their backend field exists
  // but the customFields refetch may not have repopulated initialColumns yet.
  useEffect(() => {
    if (!options?.pruneOrphanCustomColumns) return;
    const initialKeys = new Set(initialColumns.map(c => c.key));
    const orphanKeys = persisted.customColumns
      .filter(c =>
        c.key.startsWith("custom_") &&
        !initialKeys.has(c.key) &&
        !sessionAddedKeysRef.current.has(c.key)
      )
      .map(c => c.key);
    if (orphanKeys.length === 0) return;
    const orphanSet = new Set(orphanKeys);
    setPersisted(prev => ({
      ...prev,
      customColumns: prev.customColumns.filter(c => !orphanSet.has(c.key)),
    }));
  }, [options?.pruneOrphanCustomColumns, initialColumns, persisted.customColumns, setPersisted]);

  const colMap = useMemo(() => new Map(allColumnsList.map(c => [c.key, c])), [allColumnsList]);

  const allColumns = useMemo(
    () => effectiveColumnOrder.map(k => colMap.get(k)).filter((c): c is ColumnDef => Boolean(c)),
    [effectiveColumnOrder, colMap],
  );

  const hiddenKeys = useMemo(
    () => new Set([...persisted.hiddenKeys, ...freshDefaults]),
    [persisted.hiddenKeys, freshDefaults],
  );

  const visibleColumns = useMemo(
    () => allColumns.filter(c => !hiddenKeys.has(c.key)),
    [allColumns, hiddenKeys],
  );

  const toggleSort = useCallback((key: string, dir: SortDirection) => {
    setPersisted(prev => ({
      ...prev,
      sortCol: dir === null ? null : key,
      sortDir: dir === null ? null : dir,
    }));
  }, [setPersisted]);

  const hideColumn = useCallback((key: string) => {
    setPersisted(prev =>
      prev.hiddenKeys.includes(key) ? prev : { ...prev, hiddenKeys: [...prev.hiddenKeys, key] }
    );
  }, [setPersisted]);

  const showColumn = useCallback((key: string) => {
    setPersisted(prev => ({ ...prev, hiddenKeys: prev.hiddenKeys.filter(k => k !== key) }));
  }, [setPersisted]);

  const reorderColumns = useCallback((fromKey: string, toKey: string) => {
    if (fromKey === toKey) return;
    setPersisted(prev => {
      const orderSet = new Set(prev.columnOrder);
      const missing = initialColumnsRef.current.filter(c => !orderSet.has(c.key)).map(c => c.key);
      const next = missing.length === 0 ? [...prev.columnOrder] : [...prev.columnOrder, ...missing];
      const fromIdx = next.indexOf(fromKey);
      const toIdx = next.indexOf(toKey);
      if (fromIdx === -1 || toIdx === -1) return prev;
      next.splice(fromIdx, 1);
      next.splice(toIdx, 0, fromKey);
      return { ...prev, columnOrder: next };
    });
  }, [setPersisted]);

  const addColumn = useCallback((col: ColumnDef) => {
    // Mark as session-created so pruneOrphanCustomColumns never removes it before
    // the backend custom-fields query repopulates initialColumns (see prune effect).
    sessionAddedKeysRef.current.add(col.key);
    setPersisted(prev => ({
      ...prev,
      customColumns: [...prev.customColumns.filter(c => c.key !== col.key), col],
      columnOrder: prev.columnOrder.includes(col.key) ? prev.columnOrder : [...prev.columnOrder, col.key],
    }));
  }, [setPersisted]);

  const removeColumn = useCallback((key: string) => {
    setPersisted(prev => {
      const nextWidths = { ...prev.columnWidths };
      delete nextWidths[key];
      return {
        ...prev,
        customColumns: prev.customColumns.filter(c => c.key !== key),
        columnOrder: prev.columnOrder.filter(k => k !== key),
        hiddenKeys: prev.hiddenKeys.filter(k => k !== key),
        columnWidths: nextWidths,
      };
    });
  }, [setPersisted]);

  const setColumnWidth = useCallback((key: string, width: number) => {
    setPersisted(prev => ({
      ...prev,
      columnWidths: { ...prev.columnWidths, [key]: Math.max(80, width) },
    }));
  }, [setPersisted]);

  // Measured-once frozen widths. `null` = not yet frozen (table renders in auto
  // layout so columns fit content); a record = frozen (table switches to fixed
  // layout and these widths become the source of truth). Held in session state, not
  // localStorage, so a remount re-measures the fresh first page consistently.
  const [frozenWidths, setFrozenWidths] = useState<Record<string, number> | null>(null);
  const isFrozen = frozenWidths !== null;

  const freezeColumnWidths = useCallback((measured: Record<string, number>) => {
    // Idempotent: only the first freeze per mount sticks.
    setFrozenWidths(prev => (prev !== null ? prev : measured));
  }, []);

  const perKeyDefaults = options?.defaultWidths;
  const getColumnWidth = useCallback((key: string): number => {
    const override = persisted.columnWidths[key];
    if (override != null) return override;
    const frozen = frozenWidths?.[key];
    if (frozen != null) return frozen;
    const perKey = perKeyDefaults?.[key];
    if (perKey != null) return perKey;
    // COLUMN_TYPE_WIDTHS[type] is `undefined` for a type outside ColumnType (e.g.
    // an unnormalized backend value like "string"). Coalesce so an unknown type
    // never leaks `undefined` into totalContentWidth (→ NaN table width → broken
    // layout). See columnTypeFromCustomFieldType, which prevents this upstream.
    const type = colMap.get(key)?.type;
    return (type && COLUMN_TYPE_WIDTHS[type]) || DEFAULT_COLUMN_WIDTH;
  }, [persisted.columnWidths, frozenWidths, perKeyDefaults, colMap]);

  const totalContentWidth = useMemo(
    () => visibleColumns.reduce((sum, c) => sum + getColumnWidth(c.key), 0),
    [visibleColumns, getColumnWidth],
  );

  const updateColumn = useCallback((
    key: string,
    updates: Partial<Pick<ColumnDef, "label" | "type" | "selectOptions" | "aiPrompt">>,
  ) => {
    setPersisted(prev => ({
      ...prev,
      customColumns: prev.customColumns.map(c => c.key === key ? { ...c, ...updates } : c),
    }));
  }, [setPersisted]);

  const numericKeys = useMemo(() => new Set(options?.numericKeys ?? []), [options?.numericKeys]);
  const sortTransforms = options?.sortTransforms ?? {};

  const sortData = useCallback(<T,>(data: T[]): T[] => {
    const col = persisted.sortCol;
    const dir = persisted.sortDir;
    if (!col || !dir) return data;
    return [...data].sort((a, b) => {
      let av = (a as any)[col];
      let bv = (b as any)[col];
      const transform = sortTransforms[col];
      if (transform) { av = transform(av); bv = transform(bv); }
      if (numericKeys.has(col) || typeof av === "number") {
        const na = typeof av === "number" ? av : parseFloat(av) || 0;
        const nb = typeof bv === "number" ? bv : parseFloat(bv) || 0;
        return dir === "asc" ? na - nb : nb - na;
      }
      const sa = String(av ?? "");
      const sb = String(bv ?? "");
      return dir === "asc" ? sa.localeCompare(sb) : sb.localeCompare(sa);
    });
  }, [persisted.sortCol, persisted.sortDir, numericKeys, sortTransforms]);

  return {
    visibleColumns,
    allColumns,
    hiddenKeys,
    sortCol: persisted.sortCol,
    sortDir: persisted.sortDir,
    columnOrder: effectiveColumnOrder,
    columnWidths: persisted.columnWidths,
    getColumnWidth,
    totalContentWidth,
    isFrozen,
    freezeColumnWidths,
    toggleSort,
    hideColumn,
    showColumn,
    reorderColumns,
    sortData,
    addColumn,
    removeColumn,
    setColumnWidth,
    updateColumn,
  };
}
